import React, { useState, useEffect } from 'react';
import { updateUserProfile, uploadAvatar, subscribeToFriendRequests, acceptFriendRequest, declineFriendRequest, resetTutorialSeen, BADGES, subscribeToUserGiftCodes, redeemGiftCode, syncNameHistory } from '../services/firestore.js';
import { Edit2, Save, X, Twitter, Instagram, Youtube, User, LogOut, Zap, Flame, Sparkles, Award, Users, Trophy, Lightbulb, Shuffle, Upload, Globe, XCircle, UserPlus, Check, Crown, HelpCircle, Gift, Music, Video, Github, Linkedin, Facebook, MessageCircle, Gamepad2, Smile, Coffee, Rocket, RotateCcw } from 'lucide-react';
import UserBadge from '../components/UserBadge.jsx';
import { parseContentWithEmotes } from '../utils/textUtils.js';
import EmotePicker from '../components/EmotePicker.jsx';
import { useEmoteTheme } from '../hooks/useEmoteTheme.js';

// ... (skipping social icons) ...

// ... inside Profile component ...

const iconMap = {
    Zap, Flame, Sparkles, Award, Users, Trophy, Lightbulb, Crown, Rocket
};

const SOCIAL_ICONS = {
    twitter: Twitter,
    instagram: Instagram,
    youtube: Youtube,
    twitch: Gamepad2, // Twitch uses Gamepad2 or custom
    tiktok: Video,
    github: Github,
    linkedin: Linkedin,
    facebook: Facebook,
    discord: MessageCircle,
    spotify: Music,
    portfolio: Globe,
    website: Globe
};

const detectPlatform = (url) => {
    if (!url) return 'website';
    const lower = url.toLowerCase();
    if (lower.match(/(twitter\.com|x\.com)/)) return 'twitter';
    if (lower.includes('instagram.com')) return 'instagram';
    if (lower.match(/(youtube\.com|youtu\.be)/)) return 'youtube';
    if (lower.includes('twitch.tv')) return 'twitch';
    if (lower.includes('tiktok.com')) return 'tiktok';
    if (lower.includes('github.com')) return 'github';
    if (lower.includes('linkedin.com')) return 'linkedin';
    if (lower.includes('facebook.com')) return 'facebook';
    if (lower.match(/(discord\.com|discord\.gg)/)) return 'discord';
    if (lower.includes('spotify.com')) return 'spotify';
    return 'website';
};

const validateLink = (url) => {
    if (!url) return { valid: true };
    if (!url.startsWith('http')) return { valid: false, error: "Must start with http:// or https://" };
    return { valid: true };
};

// Random name generator
const generateRandomName = (type) => {
    const firstWords = ['Cosmic', 'Digital', 'Neon', 'Cyber', 'Quantum', 'Electric', 'Stellar', 'Nova', 'Pixel', 'Crystal'];
    const secondWords = ['Phoenix', 'Dragon', 'Wizard', 'Ninja', 'Samurai', 'Knight', 'Warrior', 'Mage', 'Sage', 'Rogue'];

    if (type === 'first') return firstWords[Math.floor(Math.random() * firstWords.length)];
    if (type === 'second') return secondWords[Math.floor(Math.random() * secondWords.length)];
    return `${firstWords[Math.floor(Math.random() * firstWords.length)]} ${secondWords[Math.floor(Math.random() * secondWords.length)]}`;
};

const Profile = ({ user, onViewProfile, onLogout }) => {
    const { getEmoteUrl } = useEmoteTheme();
    const [editMode, setEditMode] = useState(false);
    const [friendRequests, setFriendRequests] = useState([]);
    const [editableBio, setEditableBio] = useState('');
    const [editableAvatar, setEditableAvatar] = useState('');
    const [avatarPreview, setAvatarPreview] = useState('');
    const [avatarFile, setAvatarFile] = useState(null);
    const [editableSocialLinks, setEditableSocialLinks] = useState([]); // Array now
    const [editableNameFirst, setEditableNameFirst] = useState('');
    const [editableNameSecond, setEditableNameSecond] = useState('');
    const [avatarMethod, setAvatarMethod] = useState('url');
    const [validationErrors, setValidationErrors] = useState({});
    const [saveError, setSaveError] = useState(null);
    const [confirmLogout, setConfirmLogout] = useState(false);
    const [showSubStatus, setShowSubStatus] = useState(false);
    const [showBioEmotePicker, setShowBioEmotePicker] = useState(false);

    // Dynamic Limits
    const tier = user?.subscriptionTier || 'global';
    const linkLimit = tier === 'mega_fan' ? 30 : (tier === 'super_subscriber' ? 20 : (tier === 'subscriber' ? 10 : 5));

    // Gift Logic State
    const [myCodes, setMyCodes] = useState([]);
    const [redeemCode, setRedeemCode] = useState('');
    const [redeemStatus, setRedeemStatus] = useState({ type: '', msg: '' });

    // Load Gift Codes ALWAYS to show notification Badge
    useEffect(() => {
        if (!user?.uid) return;
        const unsubscribe = subscribeToUserGiftCodes(user.uid, (codes) => {
            setMyCodes(codes);
        });
        return () => unsubscribe();
    }, [user?.uid]);

    // Active Gift Code Count for Badge
    const activeGiftCount = myCodes.filter(c => c.status === 'active').length;

    const handleRedeem = async (e) => {
        e.preventDefault();
        setRedeemStatus({ type: 'info', msg: 'Redeeming...' });
        try {
            const result = await redeemGiftCode(user.uid, redeemCode);
            setRedeemStatus({ type: 'success', msg: `Redeemed! You now have ${result.durationDays || 30} days of ${result.tier === 'super_fan' ? 'Super Fan' : 'Supporter'} access!` });
            setRedeemCode('');
        } catch (error) {
            setRedeemStatus({ type: 'error', msg: error.message });
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        alert("Code copied to clipboard!");
    };

    useEffect(() => {
        if (user?.uid) {
            const unsubscribe = subscribeToFriendRequests(user.uid, setFriendRequests);
            return () => unsubscribe();
        }
    }, [user?.uid]);

    const stats = user?.stats || {
        sparksCreated: 0,
        likesReceived: 0,
        reactionsReceived: 0,
        streak: 0,
        challengesCompleted: 0,
        museUsage: 0
    };

    const userBadges = BADGES.map(badgeDef => {
        const unlockedBadge = user?.badges?.find(b => b.id === badgeDef.id);
        return {
            ...badgeDef,
            unlocked: unlockedBadge?.unlocked || false,
            unlockedAt: unlockedBadge?.unlockedAt
        };
    });

    const handleAcceptRequest = async (request) => {
        if (!user?.uid) return;
        try {
            await acceptFriendRequest(request.id, request.from, user.uid);
        } catch (e) {
            console.error(e);
        }
    };

    const handleDeclineRequest = async (requestId) => {
        try {
            await declineFriendRequest(requestId);
        } catch (e) {
            console.error(e);
        }
    };

    const handleEditClick = () => {
        if (!user) return;
        const nameParts = (user.name || '').split(' ');
        setEditableNameFirst(nameParts[0] || '');
        setEditableNameSecond(nameParts[1] || '');
        setEditableAvatar(user.avatar || '');
        setAvatarPreview(user.avatar || '');
        setAvatarFile(null);
        setEditableBio(user.bio || '');

        // MIGRATION LOGIC: Convert old Object to new Array if needed
        let initialLinks = [];
        if (Array.isArray(user.links)) {
            initialLinks = [...user.links];
        } else if (user.socialLinks) {
            // Convert old map to array
            Object.entries(user.socialLinks).forEach(([key, url]) => {
                if (url) initialLinks.push({ platform: key, url: url });
            });
        }
        setEditableSocialLinks(initialLinks);

        setValidationErrors({});
        setSaveError(null);
        setEditMode(true);
    };

    const handleAvatarUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    const maxSize = 500;
                    if (width > height) {
                        if (width > maxSize) {
                            height *= maxSize / width;
                            width = maxSize;
                        }
                    } else {
                        if (height > maxSize) {
                            width *= maxSize / height;
                            height = maxSize;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
                    setAvatarPreview(compressedBase64);

                    canvas.toBlob((blob) => {
                        const newFile = new File([blob], file.name, { type: 'image/jpeg' });
                        setAvatarFile(newFile);
                    }, 'image/jpeg', 0.7);
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLinkChange = (index, value) => {
        const newLinks = [...editableSocialLinks];
        newLinks[index].url = value;
        newLinks[index].platform = detectPlatform(value); // Auto-detect
        setEditableSocialLinks(newLinks);

        const validation = validateLink(value);
        if (!validation.valid && value) {
            setValidationErrors(prev => ({ ...prev, [`link_${index}`]: validation.error }));
        } else {
            const newErrors = { ...validationErrors };
            delete newErrors[`link_${index}`];
            setValidationErrors(newErrors);
        }
    };

    const handleAddLink = () => {
        if (editableSocialLinks.length >= linkLimit) return;
        setEditableSocialLinks([...editableSocialLinks, { platform: 'website', url: '' }]);
    };

    const handleRemoveLink = (index) => {
        const newLinks = [...editableSocialLinks];
        newLinks.splice(index, 1);
        setEditableSocialLinks(newLinks);
        // Clear error for this index if exists (rough approximation, clearing all for simplicity is safer or just letting them hang)
        const newErrors = { ...validationErrors };
        delete newErrors[`link_${index}`];
        setValidationErrors(newErrors);
    };

    const handleSave = async () => {
        try {
            setSaveError(null);
            const fullName = `${editableNameFirst} ${editableNameSecond}`.trim();

            let finalAvatarUrl = editableAvatar;

            if (avatarFile) {
                try {
                    finalAvatarUrl = await uploadAvatar(user.uid, avatarFile);
                } catch (uploadErr) {
                    throw new Error(`Image Upload Failed: ${uploadErr.message}`);
                }
            } else if (avatarMethod === 'url' && editableAvatar !== user.avatar) {
                finalAvatarUrl = editableAvatar;
            }

            await updateUserProfile(user.uid, {
                name: fullName,
                avatar: finalAvatarUrl,
                bio: editableBio,
                links: editableSocialLinks // Saving new Array format
                // We leave socialLinks (legacy) alone or could set to null. Better to just start using 'links'.
            });

            setEditMode(false);
        } catch (error) {
            console.error('Overall save error:', error);
            setSaveError(error.message);
        }
    };

    const handleCancel = () => {
        setEditMode(false);
    };

    const handleBioEmoteSelect = (emoji) => {
        // EmotePicker returns a string (code for custom, emoji char for native)
        if (typeof emoji === 'string') {
            setEditableBio(prev => prev + emoji);
        } else {
            // Fallback if signature changes
            setEditableBio(prev => prev + (emoji.code || emoji.native || emoji));
        }
        setShowBioEmotePicker(false);
    };

    if (!user) return React.createElement('div', { className: 'animate-pulse' }, "Loading profile...");

    // iconMap is already defined outside component

    // FILTERED BADGES LOGIC
    const filteredBadges = BADGES.filter(badge => {
        // MYSTERY LOGIC: 'vip_gold_crown' only visible to Mega Fans
        if (badge.id === 'vip_gold_crown' && user.subscriptionTier !== 'mega_fan') return false;
        return true;
    }).map(badgeDef => {
        const unlockedBadge = user.badges?.find(b => b.id === badgeDef.id);
        return {
            ...badgeDef,
            unlocked: unlockedBadge?.unlocked || false,
            unlockedAt: unlockedBadge?.unlockedAt // Keep for tooltip or details if needed
        };
    });

    return React.createElement('div', { className: 'animate-fade-in', style: { maxWidth: '700px', margin: '0 auto' } },
        React.createElement('div', { className: 'glass-panel', style: { padding: '40px', textAlign: 'center', marginBottom: '20px', position: 'relative', overflow: 'hidden' } },
            React.createElement('div', { style: { position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)', zIndex: 0 } }),

            onLogout && React.createElement('button', {
                onClick: () => {
                    if (confirmLogout) {
                        onLogout();
                        setConfirmLogout(false);
                    } else {
                        setConfirmLogout(true);
                        setTimeout(() => setConfirmLogout(false), 3000);
                    }
                },
                title: confirmLogout ? "Click to Confirm" : "Sign Out",
                style: {
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    zIndex: 10,
                    background: confirmLogout ? 'var(--secondary)' : 'none',
                    borderRadius: '4px',
                    border: confirmLogout ? 'none' : '1px solid var(--border)',
                    color: confirmLogout ? 'white' : '#999',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    fontSize: '14px',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }
            }, confirmLogout ? "Sure?" : React.createElement(React.Fragment, null, React.createElement(LogOut, { size: 16 }), "Sign Out")),
            React.createElement('img', {
                src: user.avatar,
                style: { width: '120px', height: '120px', borderRadius: '50%', border: '4px solid var(--primary)', marginBottom: '20px', position: 'relative', zIndex: 1, objectFit: 'cover' }
            }),
            React.createElement('h2', { style: { fontSize: '32px', marginBottom: '10px', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' } },
                user.name,
                React.createElement(UserBadge, { user: user, size: 40 })
            ),
            React.createElement('p', { style: { color: 'var(--text-muted)', fontSize: '18px', marginBottom: '20px', position: 'relative', zIndex: 1, whiteSpace: 'pre-wrap' } },
                user.bio ? parseContentWithEmotes(user.bio, getEmoteUrl) : "No bio yet."
            ),



            React.createElement('div', { style: { display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '20px', position: 'relative', zIndex: 1, flexWrap: 'wrap' } },
                (() => {
                    const links = user.links || (user.socialLinks ? Object.entries(user.socialLinks).map(([k, v]) => ({ platform: k, url: v })) : []);
                    return links.map((link, i) => {
                        // Standardize platform to handle old 'portfolio' -> 'website' or just keep classes.
                        // We added CSS for .portfolio, so it works.
                        if (!link.url) return null;
                        const PlatformIcon = SOCIAL_ICONS[link.platform] || Globe;
                        return React.createElement('a', {
                            key: i,
                            href: link.url,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            className: `social-link ${link.platform}`
                        }, React.createElement(PlatformIcon, { size: 20 }));
                    });
                })()
            ),

            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '20px', position: 'relative', zIndex: 1 } },
                React.createElement('div', { style: { textAlign: 'center' } },
                    React.createElement('div', { style: { fontSize: '28px', fontWeight: 'bold', color: 'var(--primary)' } }, Math.max(0, stats.sparksCreated || 0)),
                    React.createElement('div', { style: { fontSize: '11px', color: '#999', textTransform: 'uppercase', marginTop: '5px' } }, "Sparks")
                ),
                React.createElement('div', { style: { textAlign: 'center' } },
                    React.createElement('div', { style: { fontSize: '28px', fontWeight: 'bold', color: 'var(--secondary)' } }, Math.max(0, stats.likesReceived || 0)),
                    React.createElement('div', { style: { fontSize: '11px', color: '#999', textTransform: 'uppercase', marginTop: '5px' } }, "Likes")
                ),
                React.createElement('div', { style: { textAlign: 'center' } },
                    React.createElement('div', { style: { fontSize: '28px', fontWeight: 'bold', color: '#a78bfa' } }, Math.max(0, stats.reactionsReceived || 0)),
                    React.createElement('div', { style: { fontSize: '11px', color: '#999', textTransform: 'uppercase', marginTop: '5px' } }, "Reactions")
                ),
                React.createElement('div', { style: { textAlign: 'center' } },
                    React.createElement('div', { style: { fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' } }, Math.max(0, stats.streak || 0)),
                    React.createElement('div', { style: { fontSize: '11px', color: '#999', textTransform: 'uppercase', marginTop: '5px' } }, "Day Streak")
                ),
                React.createElement('div', { style: { textAlign: 'center' } },
                    React.createElement('div', { style: { fontSize: '28px', fontWeight: 'bold', color: '#06b6d4' } }, Math.max(0, stats.challengesCompleted || 0)),
                    React.createElement('div', { style: { fontSize: '11px', color: '#999', textTransform: 'uppercase', marginTop: '5px' } }, "Challenges")
                )
            )
        ),

        !editMode && React.createElement('div', { style: { marginBottom: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '15px' } },
            React.createElement('button', {
                onClick: handleEditClick,
                className: 'btn-ghost',
                style: { padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#999' }
            }, React.createElement(Edit2, { size: 18 }), "Edit Profile"),

            React.createElement('button', {
                onClick: () => setShowSubStatus(!showSubStatus),
                className: 'btn-ghost',
                style: { padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--primary)', position: 'relative' }
            },
                React.createElement(Zap, { size: 18 }),
                "Subscription Status",
                // BADGE ON BUTTON
                activeGiftCount > 0 && React.createElement('div', {
                    style: {
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        background: '#ec4899',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid var(--bg-main)',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                        flexShrink: 0
                    }
                }, React.createElement(Gift, { size: 10, color: 'white' }))
            )
        ),

        showSubStatus && React.createElement('div', { className: 'glass-panel animate-fade-in', style: { padding: '25px', marginBottom: '20px', border: '1px solid var(--primary)' } },
            React.createElement('h3', { style: { marginBottom: '15px', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '10px' } },
                React.createElement(Crown, { size: 20, color: 'var(--primary)' }), "Your Membership"
            ),

            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '15px' } },
                // Mega Fan Status (Hidden Tier)
                user.subscriptionTier === 'mega_fan' && React.createElement('div', { style: { padding: '15px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '10px', border: '1px solid #f59e0b', marginBottom: '5px' } },
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' } },
                        React.createElement('div', { style: { fontWeight: 'bold', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px' } },
                            React.createElement(Crown, { size: 18, color: '#f59e0b' }),
                            "Mega Fan Access"
                        ),
                        React.createElement('span', { style: { fontSize: '0.8rem', background: '#f59e0b', color: 'white', padding: '2px 8px', borderRadius: '10px' } }, "ACTIVE")
                    ),
                    React.createElement('div', { style: { fontSize: '0.9rem', color: '#ccc' } },
                        (user.superSubscriberEnd && user.subscriberEnd)
                            ? `Expires: ${new Date(Math.min(user.superSubscriberEnd.seconds * 1000, user.subscriberEnd.seconds * 1000)).toLocaleDateString()}`
                            : "Active"
                    )
                ),

                // Super Fan Status
                React.createElement('div', { style: { padding: '15px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '10px', border: user.subscriptionTier === 'super_subscriber' ? '1px solid var(--primary)' : '1px solid transparent', opacity: user.superSubscriberEnd ? 1 : 0.5 } },
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' } },
                        React.createElement('div', { style: { fontWeight: 'bold', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '8px' } },
                            React.createElement(Crown, { size: 18, color: 'var(--primary)' }),
                            "Super Fan Access"
                        ),
                        user.subscriptionTier === 'super_subscriber' && React.createElement('span', { style: { fontSize: '0.8rem', background: 'var(--primary)', color: 'white', padding: '2px 8px', borderRadius: '10px' } }, "ACTIVE")
                    ),
                    React.createElement('div', { style: { fontSize: '0.9rem', color: '#ccc' } },
                        user.superSubscriberEnd
                            ? `Expires: ${new Date(user.superSubscriberEnd.seconds * 1000).toLocaleDateString()}`
                            : "Not Active"
                    )
                ),

                // Subscriber Status
                React.createElement('div', { style: { padding: '15px', background: 'rgba(236, 72, 153, 0.1)', borderRadius: '10px', border: user.subscriptionTier === 'subscriber' ? '1px solid var(--secondary)' : '1px solid transparent', opacity: user.subscriberEnd ? 1 : 0.5 } },
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' } },
                        React.createElement('div', { style: { fontWeight: 'bold', color: 'var(--secondary)', display: 'flex', alignItems: 'center', gap: '8px' } },
                            React.createElement(Coffee, { size: 18, color: 'var(--secondary)' }),
                            "Supporter Access"
                        ),
                        user.subscriptionTier === 'subscriber' && React.createElement('span', { style: { fontSize: '0.8rem', background: 'var(--secondary)', color: 'white', padding: '2px 8px', borderRadius: '10px' } }, "ACTIVE")
                    ),
                    React.createElement('div', { style: { fontSize: '0.9rem', color: '#ccc' } },
                        user.subscriberEnd
                            ? (user.subscriptionTier === 'super_subscriber' ? `Resumes after Super Fan expires. Valid until: ${new Date(user.subscriberEnd.seconds * 1000).toLocaleDateString()}` : `Expires: ${new Date(user.subscriberEnd.seconds * 1000).toLocaleDateString()}`)
                            : "Not Active"
                    )
                ),

                // GIFT REDEMPTION SECTION
                React.createElement('div', { style: { marginTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' } },
                    React.createElement('h4', { style: { marginBottom: '10px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' } },
                        React.createElement(Gift, { size: 16, color: 'var(--primary)' }), "Redeem Gift Code"
                    ),
                    React.createElement('form', { onSubmit: handleRedeem, style: { display: 'flex', gap: '10px' } },
                        React.createElement('input', {
                            type: 'text',
                            placeholder: "GIFT-XXXX-XXXX",
                            value: redeemCode,
                            onChange: (e) => setRedeemCode(e.target.value),
                            style: { flex: 1, padding: '8px 12px', borderRadius: '6px', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', color: 'white' }
                        }),
                        React.createElement('button', { type: 'submit', className: 'btn-secondary', disabled: !redeemCode, style: { padding: '8px 16px' } }, "Redeem")
                    ),
                    redeemStatus.msg && React.createElement('div', { style: { marginTop: '8px', fontSize: '13px', color: redeemStatus.type === 'error' ? '#f44336' : '#4caf50' } }, redeemStatus.msg)
                ),

                // MY PURCHASED GIFTS SECTION
                React.createElement('div', { style: { marginTop: '20px' } },
                    React.createElement('h4', { style: { marginBottom: '10px', fontSize: '16px', color: '#ccc' } }, "My Purchased Gifts"),
                    myCodes.length === 0 ?
                        React.createElement('div', { style: { fontSize: '13px', color: '#666', fontStyle: 'italic' } }, "No gifts purchased yet.") :
                        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                            myCodes.map(gift =>
                                React.createElement('div', { key: gift.id, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '6px', fontSize: '13px', border: gift.status === 'active' ? '1px solid rgba(236, 72, 153, 0.3)' : '1px solid transparent' } },
                                    React.createElement('div', {},
                                        React.createElement('div', { style: { fontWeight: 'bold', color: gift.tier === 'super_fan' ? '#8b5cf6' : '#ec4899', display: 'flex', alignItems: 'center', gap: '6px' } },
                                            gift.tier === 'super_fan' ? 'Super Fan' : 'Supporter',
                                            // BADGE ON LIST ITEM
                                            gift.status === 'active' && React.createElement('div', {
                                                style: {
                                                    background: '#ec4899',
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                },
                                                title: 'Unused Gift'
                                            }, React.createElement(Gift, { size: 9, color: 'white' }))
                                        ),
                                        React.createElement('div', {
                                            onClick: () => copyCode(gift.code),
                                            style: { fontFamily: 'monospace', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', color: '#ccc' },
                                            title: "Click to Copy"
                                        }, gift.code, React.createElement('span', { style: { fontSize: '10px', opacity: 0.7 } }, "📋"))
                                    ),
                                    React.createElement('div', { style: { textAlign: 'right' } },
                                        React.createElement('span', {
                                            style: {
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                background: gift.status === 'active' ? '#4caf50' : '#555',
                                                color: gift.status === 'active' ? 'white' : '#ccc'
                                            }
                                        }, gift.status.toUpperCase()),
                                        gift.status === 'redeemed' && React.createElement('div', { style: { fontSize: '10px', color: '#666', marginTop: '2px' } },
                                            gift.redeemedAt?.toDate().toLocaleDateString()
                                        )
                                    )
                                )
                            )
                        )
                )
            )
        ),

        editMode && React.createElement('div', { className: 'glass-panel animate-fade-in', style: { padding: '30px', marginBottom: '20px' } },
            React.createElement('h3', { style: { marginBottom: '20px', fontSize: '20px' } }, "Edit Profile"),

            saveError && React.createElement('div', { style: { padding: '12px', marginBottom: '15px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', borderRadius: '8px', color: '#ef4444', fontSize: '14px' } }, saveError),

            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '20px' } },
                React.createElement('div', {},
                    React.createElement('label', { style: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' } }, "Username (Two Words)"),
                    React.createElement('div', { style: { display: 'flex', gap: '10px', marginBottom: '8px' } },
                        React.createElement('input', {
                            type: 'text',
                            value: editableNameFirst,
                            onChange: (e) => setEditableNameFirst(e.target.value),
                            placeholder: "First word",
                            style: { flex: 1, padding: '10px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: 'var(--text-main)', outline: 'none' }
                        }),
                        React.createElement('button', {
                            type: 'button',
                            onClick: () => setEditableNameFirst(generateRandomName('first')),
                            className: 'btn-ghost',
                            title: 'Random',
                            style: { padding: '10px', display: 'flex', alignItems: 'center', color: '#999' }
                        }, React.createElement(Shuffle, { size: 16 })),
                        React.createElement('input', {
                            type: 'text',
                            value: editableNameSecond,
                            onChange: (e) => setEditableNameSecond(e.target.value),
                            placeholder: "Second word",
                            style: { flex: 1, padding: '10px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: 'var(--text-main)', outline: 'none' }
                        }),
                        React.createElement('button', {
                            type: 'button',
                            onClick: () => setEditableNameSecond(generateRandomName('second')),
                            className: 'btn-ghost',
                            title: 'Random',
                            style: { padding: '10px', display: 'flex', alignItems: 'center', color: '#999' }
                        }, React.createElement(Shuffle, { size: 16 }))
                    ),
                    React.createElement('button', {
                        type: 'button',
                        onClick: () => { const name = generateRandomName('both'); const parts = name.split(' '); setEditableNameFirst(parts[0]); setEditableNameSecond(parts[1]); },
                        className: 'btn-ghost',
                        style: { padding: '8px 12px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#999' }
                    }, React.createElement(Shuffle, { size: 14 }), "Randomize Both"),

                    // SYNC BUTTON
                    React.createElement('button', {
                        type: 'button',
                        onClick: async () => {
                            if (!user?.uid) return;
                            const newName = `${editableNameFirst} ${editableNameSecond}`.trim();
                            if (!newName) return;
                            if (window.confirm(`Update all your past Sparks to use the name "${newName}"?`)) {
                                try {
                                    const count = await syncNameHistory(user.uid, newName);
                                    alert(`Success! Updated ${count} historical sparks.`);
                                } catch (e) {
                                    alert("Error syncing: " + e.message);
                                }
                            }
                        },
                        className: 'btn-ghost',
                        style: { marginLeft: '10px', padding: '8px 12px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--primary)', border: '1px solid var(--primary)', borderRadius: '8px' }
                    }, React.createElement(RotateCcw, { size: 14 }), "Sync Name to Past Posts")
                ),

                React.createElement('div', {},
                    React.createElement('label', { style: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' } }, "Avatar"),
                    React.createElement('div', { style: { display: 'flex', gap: '10px', marginBottom: '10px' } },
                        React.createElement('button', {
                            type: 'button',
                            onClick: () => setAvatarMethod('url'),
                            className: avatarMethod === 'url' ? 'btn' : 'btn-ghost',
                            style: { padding: '8px 16px', fontSize: '14px', color: avatarMethod === 'url' ? 'white' : '#999' }
                        }, "URL"),
                        React.createElement('button', {
                            type: 'button',
                            onClick: () => setAvatarMethod('upload'),
                            className: avatarMethod === 'upload' ? 'btn' : 'btn-ghost',
                            style: { padding: '8px 16px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: avatarMethod === 'upload' ? 'white' : '#999' }
                        }, React.createElement(Upload, { size: 14 }), "Upload")
                    ),
                    avatarMethod === 'url' ? React.createElement('input', {
                        type: 'text',
                        value: editableAvatar,
                        onChange: (e) => { setEditableAvatar(e.target.value); setAvatarPreview(e.target.value); setAvatarFile(null); },
                        placeholder: "https://example.com/your-avatar.jpg",
                        style: { width: '100%', padding: '12px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: 'var(--text-main)', outline: 'none' }
                    }) : React.createElement('input', {
                        type: 'file',
                        accept: 'image/*',
                        onChange: handleAvatarUpload,
                        style: { width: '100%', padding: '12px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: 'var(--text-main)', outline: 'none' }
                    })
                ),

                React.createElement('div', {},
                    React.createElement('label', { style: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' } }, "Bio"),
                    React.createElement('div', { style: { position: 'relative' } },
                        React.createElement('textarea', {
                            value: editableBio,
                            onChange: (e) => setEditableBio(e.target.value),
                            placeholder: "Tell us about yourself...",
                            style: { width: '100%', padding: '10px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: 'var(--text-main)', outline: 'none', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' }
                        }),
                        React.createElement('button', {
                            onClick: () => setShowBioEmotePicker(!showBioEmotePicker),
                            className: 'btn-ghost',
                            style: { position: 'absolute', bottom: '10px', right: '10px', padding: '5px' },
                            title: "Add Emote"
                        }, React.createElement(Smile, { size: 18, color: '#999' })),

                        showBioEmotePicker && React.createElement('div', { style: { position: 'absolute', bottom: '100%', right: 0, zIndex: 50, marginBottom: '5px' } },
                            React.createElement(EmotePicker, {
                                onSelect: handleBioEmoteSelect,
                                onClose: () => setShowBioEmotePicker(false),
                                userTier: tier,
                                user: user,
                                mode: 'chat'
                            })
                        )
                    ),

                    // Live Preview
                    editableBio && React.createElement('div', { style: { marginTop: '8px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', fontSize: '14px', color: '#ccc' } },
                        React.createElement('div', { style: { fontSize: '10px', textTransform: 'uppercase', color: '#666', marginBottom: '4px' } }, "Preview"),
                        parseContentWithEmotes(editableBio, getEmoteUrl)
                    )
                ),

                React.createElement('div', {},
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' } },
                        React.createElement('label', { style: { fontSize: '14px', fontWeight: 'bold' } }, "Social Links"),
                        React.createElement('span', { style: { fontSize: '12px', color: (editableSocialLinks.length >= linkLimit) ? '#ef4444' : '#999' } },
                            `${editableSocialLinks.length} / ${linkLimit} Links`
                        )
                    ),

                    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px' } },
                        editableSocialLinks.map((link, index) => {
                            const PlatformIcon = SOCIAL_ICONS[link.platform] || Globe;
                            return React.createElement('div', { key: index, style: { display: 'flex', gap: '10px', alignItems: 'center' } },
                                React.createElement('div', {
                                    className: `social-icon ${link.platform}`,
                                    style: {
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        background: 'rgba(255,255,255,0.05)',
                                        border: '1px solid var(--border)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#999'
                                    }
                                }, React.createElement(PlatformIcon, { size: 20 })),

                                React.createElement('input', {
                                    type: 'text',
                                    value: link.url,
                                    onChange: (e) => handleLinkChange(index, e.target.value),
                                    placeholder: "https://...",
                                    style: { flex: 1, padding: '10px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: validationErrors[`link_${index}`] ? '1px solid #ef4444' : '1px solid var(--border)', color: 'var(--text-main)', outline: 'none' }
                                }),

                                React.createElement('button', {
                                    onClick: () => handleRemoveLink(index),
                                    className: 'btn-ghost',
                                    style: { padding: '10px', color: '#ef4444' },
                                    title: "Remove Link"
                                }, React.createElement(X, { size: 18 }))
                            );
                        })
                    ),

                    editableSocialLinks.length < linkLimit ? React.createElement('button', {
                        onClick: handleAddLink,
                        className: 'btn-ghost',
                        style: { width: '100%', marginTop: '10px', padding: '10px', border: '1px dashed var(--border)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }
                    }, React.createElement(Zap, { size: 16 }), "Add Link") : React.createElement('div', {
                        style: { marginTop: '10px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', fontSize: '12px', color: '#999', textAlign: 'center' }
                    }, "Limit reached. Upgrade for more!")
                ),

                React.createElement('div', { style: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' } },
                    React.createElement('button', {
                        onClick: handleCancel,
                        className: 'btn-ghost',
                        style: { padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '6px', color: '#999' }
                    }, React.createElement(XCircle, { size: 16 }), "Cancel"),
                    React.createElement('button', {
                        onClick: handleSave,
                        className: 'btn',
                        disabled: Object.keys(validationErrors).length > 0,
                        style: { padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '6px', opacity: Object.keys(validationErrors).length > 0 ? 0.5 : 1 }
                    }, React.createElement(Save, { size: 16 }), "Save Changes")
                )
            )
        ),

        friendRequests.length > 0 && React.createElement('div', { className: 'glass-panel', style: { padding: '25px', marginBottom: '20px' } },
            React.createElement('h3', { style: { marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px' } },
                React.createElement(UserPlus, { size: 22, color: 'var(--primary)' }), "Friend Requests"
            ),
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '12px' } },
                friendRequests.map(request =>
                    React.createElement('div', {
                        key: request.id,
                        style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }
                    },
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
                            React.createElement('img', {
                                src: request.fromAvatar || request.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${request.from}`,
                                style: { width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }
                            }),
                            React.createElement('button', {
                                onClick: () => onViewProfile && onViewProfile(request.from),
                                style: { fontWeight: 'bold', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'transparent', transition: 'text-decoration-color 0.2s' },
                                onMouseEnter: (e) => e.target.style.textDecorationColor = 'currentColor',
                                onMouseLeave: (e) => e.target.style.textDecorationColor = 'transparent'
                            }, request.fromName || request.name || 'Unknown User')
                        ),
                        React.createElement('div', { style: { display: 'flex', gap: '8px' } },
                            React.createElement('button', {
                                onClick: () => handleAcceptRequest(request),
                                className: 'btn',
                                style: { padding: '6px 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }
                            }, React.createElement(Check, { size: 16 }), "Accept"),
                            React.createElement('button', {
                                onClick: () => handleDeclineRequest(request.id),
                                className: 'btn-ghost',
                                style: { padding: '6px 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: '#999' }
                            }, React.createElement(X, { size: 16 }), "Decline")
                        )
                    )
                )
            )
        ),

        React.createElement('div', { className: 'glass-panel', style: { padding: '30px', marginBottom: '20px' } },
            React.createElement('h3', { style: { marginBottom: '20px', fontSize: '20px' } }, "Badges"),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '15px' } },
                filteredBadges.map(badge => {
                    const IconComponent = iconMap[badge.icon] || Zap;
                    return React.createElement('div', {
                        key: badge.id,
                        title: badge.unlocked ? (badge.description || badge.name) : "Locked", // Tooltip: Standard description if unlocked (matches Super Admin view)
                        style: {
                            padding: '15px',
                            background: badge.unlocked ? `rgba(${parseInt(badge.color.slice(1, 3), 16)}, ${parseInt(badge.color.slice(3, 5), 16)}, ${parseInt(badge.color.slice(5, 7), 16)}, 0.1)` : 'rgba(255,255,255,0.03)',
                            borderRadius: '10px',
                            border: badge.unlocked ? `1px solid ${badge.color}` : '1px solid rgba(255,255,255,0.1)',
                            textAlign: 'center',
                            opacity: badge.unlocked ? 1 : 0.5,
                            transition: 'all 0.2s',
                            cursor: 'help' // Cursor added to indicate tooltip
                        }
                    },
                        React.createElement('div', { style: { marginBottom: '8px' } }, React.createElement(IconComponent, { size: 24, color: badge.unlocked ? badge.color : '#666' })),
                        React.createElement('div', { style: { fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' } }, badge.name),
                        // Only show description on card for Super Admins
                        user?.isSuperAdmin && React.createElement('div', { style: { fontSize: '10px', color: '#999' } }, badge.description)
                    );
                })
            )
        ),

        // Reset Tutorial Button
        React.createElement('div', { style: { marginTop: '40px', textAlign: 'center' } },
            React.createElement('button', {
                onClick: async () => {
                    await resetTutorialSeen(user.uid);
                    window.location.reload();
                },
                className: 'btn',
                style: {
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(59, 130, 246, 0.1))',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    borderRadius: '12px',
                    color: '#06b6d4',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)'
                },
                onMouseEnter: (e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(6, 182, 212, 0.2)';
                    e.target.style.background = 'linear-gradient(135deg, rgba(6, 182, 212, 0.2), rgba(59, 130, 246, 0.2))';
                },
                onMouseLeave: (e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(6, 182, 212, 0.1)';
                    e.target.style.background = 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(59, 130, 246, 0.1))';
                }
            }, React.createElement(HelpCircle, { size: 18 }), "Replay Onboarding")
        )
    );
};

export default Profile;
